import mkDebug from 'debug'

const debug = mkDebug('FC:NewNewCaseForm')

class NewNewCaseForm {
  constructor(form) {
    debug("Initialising")
    this.form = form
    this.sections = Array.from(
      form.querySelectorAll('[data-checklist-section]'),
      (node) => new FormSection(node, form),
    )
    this.checklist = new Checklist()
    this.currentSection = this.initialCurrentSection()

    this.onSectionFocus = this.onSectionFocus.bind(this)
    this.onSectionBlur = this.onSectionBlur.bind(this)
    this.onClusterTreeUpdate = this.onClusterTreeUpdate.bind(this)

    this.sections.forEach((section) => {
      section.onFocus(this.onSectionFocus)
      section.onBlur(this.onSectionBlur)
    })
    this.sections.forEach((section) => {
      section.onValidityChange(() => this.onSectionValidityChange(section))
    })
    document.addEventListener('clusterTreeUpdate', this.onClusterTreeUpdate)
    this.updateDetailsCount()
    this.focusInitialFormElement()
  }

  initialCurrentSection() {
    return this.sections.find((section) => {
      return this.checklist.current(section.key)
    })
  }

  detailsSection() {
    return this.sections.find((section) => section.key === 'details')
  }

  updateDetailsCount() {
    debug("updating details count")
    const details = this.detailsSection()
    if (details) {
      this.checklist.updateCompletedRequiredFields(details.completedRequiredFields())
    }
  }

  focusInitialFormElement() {
    const section = this.currentSection || this.sections[0]
    const element = section?.firstInvalidElement() || section?.firstElement()
    debug("focusing initial form element: section=%O element=%O", section, element)
    if (element) {
      element.focus()
    }
  }

  onSectionFocus(section) {
    debug("section focused %O", section)
    if (section === this.currentSection) {
      this.updateActiveStatus(section)
      return
    }

    this.currentSection = section
    this.updateActiveStatus(section)
  }

  onSectionBlur(section, relatedTarget) {
    debug("section blurred %O", section)
    if (relatedTarget && section.contains(relatedTarget)) {
      return
    }

    this.finalize(section)
    if (section === this.currentSection) {
      this.currentSection = null
    }
  }

  finalize(section) {
    debug("finalizing section %O", section)
    section.hasBeenLeft = true
    this.updateFinalizedStatus(section)
  }

  updateFinalizedStatus(section) {
    let incompleteStatus = 'invalid'
    if (section.key === 'urgency') {
      incompleteStatus = 'skipped'
    }
    this.checklist.updateStatus(
      section.key,
      section.valid() ? 'completed' : incompleteStatus,
    )
  }

  onClusterTreeUpdate(event) {
    debug("cluster tree updated %O", event)
    const assets = this.sections.find((section) => section.key === 'assets')
    if (assets) {
      assets.hasSelectedAsset = event.detail.selectedItems.some((item) => {
        return item.type === 'Asset' || (
          item.type === 'AssetGroup' && this.containsAsset(item)
        )
      })
      this.onSectionValidityChange(assets)
    }
  }

  onSectionValidityChange(section) {
    if (section === this.detailsSection()) this.updateDetailsCount()
    if (section === this.currentSection) {
      this.updateActiveStatus(section)
    } else if (section.hasBeenLeft) {
      this.updateFinalizedStatus(section)
    }
  }

  updateActiveStatus(section) {
    let status
    if (section.valid()) {
      status = 'completed'
    } else if (section.hasBeenLeft) {
      status = section.key === 'urgency' ? 'skipped' : 'invalid'
    } else {
      status = 'in-progress'
    }
    this.checklist.updateStatus(section.key, status)
  }

  containsAsset(item) {
    return item.children.some((child) => {
      return child.type === 'Asset' || this.containsAsset(child)
    })
  }
}

class Checklist {
  constructor() {
    this.items = new Map()
    document.querySelectorAll('[data-checklist-item]').forEach((node) => {
      this.items.set(node.dataset.checklistItem, node)
    })
    this.requiredFieldsCompleted = document.querySelector(
      '[data-checklist-li-completed-fields]',
    )
  }

  status(key) {
    return this.items.get(key)?.dataset.checklistLiStatus
  }

  current(key) {
    return this.items.get(key)?.dataset.checklistLiCurrent === 'true'
  }

  updateStatus(key, status) {
    const item = this.items.get(key)
    if (item) item.dataset.checklistLiStatus = status
  }

  updateCompletedRequiredFields(count) {
    if (this.requiredFieldsCompleted) {
      this.requiredFieldsCompleted.innerText = count
    }
  }
}

class FormSection {
  constructor(node, form) {
    debug("creating form section %O", node)
    this.node = node
    this.form = form
    this.key = node.dataset.checklistSection
    this.requiresAsset = node.dataset.requiresAsset === 'true'
    this.hasSelectedAsset = node.dataset.hasSelectedAsset === 'true'
    this.fields = this.sectionFields()
    this.hasBeenLeft = ['completed', 'invalid', 'skipped'].includes(
      document.querySelector(
        `[data-checklist-item="${this.key}"]`,
      )?.dataset.checklistLiStatus,
    )
  }

  sectionFields() {
    let selector
    if (this.key === 'details') {
      selector = '[data-case-subject], [data-issue-field-input]'
    } else if (this.key === 'supporting-info') {
      selector = '#case_details'
    } else if (this.key === 'urgency') {
      selector = '#urgency-checkbox'
    } else {
      return []
    }

    return Array.from(
      this.node.querySelectorAll(selector),
      (input) => new FieldInput(input),
    )
  }

  onFocus(callback) {
    this.node.addEventListener('focusin', () => callback(this))
  }

  onBlur(callback) {
    this.node.addEventListener('focusout', (event) => {
      callback(this, event.relatedTarget)
    })
  }

  contains(node) {
    return this.node.contains(node)
  }

  firstElement() {
    const element = this.node.querySelector(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
    )
    return element
  }

  firstInvalidElement() {
    const element = this.node.querySelector(
      'input:not([type="hidden"]):not([disabled]):invalid, textarea:not([disabled]):invalid, select:not([disabled]):invalid'
    )
    return element
  }

  onValidityChange(callback) {
    this.fields.forEach((field) => field.onChange(callback))
  }

  completedRequiredFields() {
    return this.fields.filter((field) => field.required() && field.filledIn()).length
  }

  valid() {
    switch (this.key) {
      case 'details':
        return this.fields.every((field) => !field.required() || field.filledIn())
      case 'assets':
        return !this.requiresAsset || this.hasSelectedAsset
      case 'supporting-info':
        return this.fields.every((field) => !field.required() || field.filledIn())
      case 'urgency':
        return this.fields.some((field) => field.checked())
      default:
        return true
    }
  }
}

class FieldInput {
  constructor(input) {
    debug("creating field input %O", input)
    this.input = input
  }

  required() {
    return this.input.required
  }

  filledIn() {
    return this.input.value.trim() !== ''
  }

  checked() {
    return this.input.checked
  }

  onChange(callback) {
    this.input.addEventListener('input', callback)
    this.input.addEventListener('change', callback)
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const form = document.querySelector('#new_new_case')
  if (form) {
    new NewNewCaseForm(form)
  }
})
